import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize(); // init
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'About', href: '#about' },
        { name: 'Purpose', href: '#vision' },
        { name: 'How We Work', href: '#how' },
        { name: 'Services', href: '#services' },
        // { name: 'Why Us', href: '#why' },
    ];

    const scrollToSection = (e, href) => {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setMobileMenuOpen(false);
        }
    };

    return (
        <>
            <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 md:pt-6 pointer-events-none">
                <motion.div
                    initial="top"
                    animate={isScrolled ? "scrolled" : "top"}
                    variants={{
                        top: {
                            width: "100%",
                            maxWidth: "80rem", // max-w-7xl
                            backgroundColor: "rgba(0, 0, 0, 0)",
                            backdropFilter: "blur(0px)",
                            borderColor: "rgba(255, 255, 255, 0)",
                            borderRadius: "0px",
                            paddingTop: "1.25rem", // py-5
                            paddingBottom: "1.25rem",
                            paddingLeft: "1.5rem",
                            paddingRight: "1.5rem",
                        },
                        scrolled: {
                            width: isMobile ? "92%" : "65%", // Different width for mobile vs desktop
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(16px)",
                            borderColor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "9999px",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            paddingLeft: "1.25rem",
                            paddingRight: "1.25rem",
                        }
                    }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 100, damping: 20 }}
                    className="flex items-center justify-between border pointer-events-auto relative overflow-hidden"
                    style={{ maxWidth: '100%' }} // responsive fallback
                >
                    {/* Falling Star Border Effect */}
                    {isScrolled && (
                        <div className="star-border-container">
                            <div className="star-border-beam"></div>
                        </div>
                    )}

                    {/* Logo */}
                    <a href="#" onClick={(e) => scrollToSection(e, '#')} className="flex-shrink-0">
                        <img
                            src={'/logo.png'}
                            alt="Ivani Logo"
                            className={`transition-all duration-300 ${isScrolled ? 'h-[28px]' : 'h-[36px]'}`}
                        />
                    </a>

                    {/* Desktop Links */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                onClick={(e) => scrollToSection(e, link.href)}
                                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative group"
                            >
                                {link.name}
                                <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-[var(--ivani-color)] scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                            </a>
                        ))}
                    </div>

                    {/* Contact Button & Mobile Toggle */}
                    <div className="flex items-center gap-4">
                        <a
                            href="https://wa.me/918618509238"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`
                                hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all duration-300
                                ${isScrolled
                                    ? 'bg-white text-black hover:bg-zinc-200'
                                    : 'bg-white/10 border border-white/10 text-white hover:bg-white/20 backdrop-blur-md'
                                }
                            `}
                        >
                            <img src={require('../assets/ivani/wa.png')} alt="" className='h-4 w-4' />
                            <span>Connect</span>
                        </a>

                        {/* Mobile Hamburger */}
                        <button
                            className="md:hidden p-2 text-white bg-white/10 rounded-full backdrop-blur-md border border-white/10"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-xl md:hidden flex justify-end"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="w-[80vw] h-full bg-[#0a0a0a] border-l border-white/10 p-6 flex flex-col shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-10">
                                <img src={'/logo.png'} alt="Logo" className="h-8" />
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 bg-white/5 rounded-full text-zinc-400 hover:text-white"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="flex flex-col gap-6">
                                {navLinks.map((link) => (
                                    <a
                                        key={link.name}
                                        href={link.href}
                                        onClick={(e) => scrollToSection(e, link.href)}
                                        className="text-2xl font-heading font-medium text-zinc-300 hover:text-white"
                                    >
                                        {link.name}
                                    </a>
                                ))}
                                <hr className="border-white/10 my-2" />
                                <a
                                    href="https://wa.me/918618509238"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-2xl font-heading font-medium text-[var(--ivani-color)]"
                                >
                                    Connect With Us
                                </a>
                            </div>

                            <div className="mt-auto">
                                <p className="text-xs text-zinc-600">© 2026 IVANI. All rights reserved.</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
